import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import { FilterService } from '@progress/kendo-angular-grid';
import { ProcessStateDto } from '@core/services/api-clients';
import { LoadProcessStatesForEntity } from '@core/engine-processes/store/actions';
import { getProcessStatesForEntity } from '@core/engine-processes/store/selectors';
import { Subject, takeUntil } from 'rxjs';
import { LodashService } from '@core/services/lodash.service';
import { EngineTranslationService } from '@core/engine-translations/services/translation.service';
import { IProcessState } from '@core/engine-processes/store/state';
import { Store } from '@ngrx/store';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';


@Component({
  selector: 'app-process-state-filter',
  templateUrl: './process-state-filter.component.html',
  styleUrls: ['./process-state-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DropDownListComponent],
})
export class ProcessStateFilterComponent implements OnInit, AfterViewInit, OnDestroy {
  static readonly EMPTY_NAME = 'View.OptionSetEmptyValue';
  static readonly NOT_EMPTY_NAME = 'View.OptionSetNotEmptyValue';

  @Input() entityId: string;
  @Input() currentFilter: CompositeFilterDescriptor;
  @Input() filterService: FilterService;
  @Input() field: string;

  processStates: ProcessStateDto[] = [];
  value: string;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store<IProcessState>,
    private readonly translationService: EngineTranslationService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

    ngOnInit(): void {
    this.store.dispatch(
      new LoadProcessStatesForEntity({
        entityId: this.entityId,
      }),
    );

    this.store
      .select(getProcessStatesForEntity(this.entityId))
      .pipe(takeUntil(this.destroy$))
      .subscribe((states) => {
        if (!states) {
          return;
        }

        this.processStates = this.addEmptyAndNotEmptyValues(states);
        this.changeDetectorRef.markForCheck();
      });
  }

ngAfterViewInit(): void {
    const currentColumnFilter = this.currentFilter.filters.find(
      (filter: FilterDescriptor) => filter.field === this.field,
    ) as FilterDescriptor;

    if (!currentColumnFilter) {
      return;
    }

    if (currentColumnFilter.operator === 'isnull') {
      this.value = ProcessStateFilterComponent.EMPTY_NAME;
    } else if (currentColumnFilter.operator === 'isnotnull') {
      this.value = ProcessStateFilterComponent.NOT_EMPTY_NAME;
    } else {
      this.value = currentColumnFilter.value;
    }

    this.changeDetectorRef.markForCheck();
  }

  onValueChange(value: string): void {
    if (value === null || value === undefined) {
      this.filterService.filter({
        filters: [],
        logic: 'and',
      });

      return;
    }

    if (value === ProcessStateFilterComponent.EMPTY_NAME) {
      this.filterService.filter({
        filters: [{ field: this.field, operator: 'isnull' }],
        logic: 'and',
      });

      return;
    }

    if (value === ProcessStateFilterComponent.NOT_EMPTY_NAME) {
      this.filterService.filter({
        filters: [{ field: this.field, operator: 'isnotnull' }],
        logic: 'and',
      });

      return;
    }

    this.filterService.filter({
      filters: [
        {
          field: this.field,
          operator: 'eq',
          value,
        },
      ],
      logic: 'and',
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private addEmptyAndNotEmptyValues(states: ProcessStateDto[]): ProcessStateDto[] {
    const result = LodashService.cloneDeep(states);

    result.push({
      id: ProcessStateFilterComponent.EMPTY_NAME,
      name: this.translationService.translateInstantly(ProcessStateFilterComponent.EMPTY_NAME),
    } as ProcessStateDto);

    result.push({
      id: ProcessStateFilterComponent.NOT_EMPTY_NAME,
      name: this.translationService.translateInstantly(ProcessStateFilterComponent.NOT_EMPTY_NAME),
    } as ProcessStateDto);

    return result;
  }
}
